import type { Object3D, Scene } from 'three';
import { Box3, Euler, Quaternion, Vector3 } from 'three';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';

import type { ModelManager } from '../model-manager.class';
import type { ModelLoadOptions } from '../types';
import { Z_FIGHTING } from '../types';
import { applyShadows, applyWireframeToModel, applyEnvironmentMapToModel, adjustSingleMaterial, createSuppressedWarn, smoothNormalsForMetalMeshes } from '../helpers';

export type LoadColladaParams = {
	options: ModelLoadOptions;
};

const applyModelOptions = (model: Object3D, self: ModelManager, options: ModelLoadOptions): void => {
	const { position, rotation, scale, metadata, parent, castShadow, receiveShadow } = options;
	const { config } = self;

	model.scale.setScalar(scale ? (typeof scale === 'number' ? scale : 10) : 10);

	if (position) {
		model.position.set(
			position.x,
			position.y * -1 + Z_FIGHTING,
			position.z * -1 + Z_FIGHTING
		);
	}

	if (rotation) {
		const mr = metadata?.modelRotation as { x: number; y: number; z: number } | undefined;
		const hasMR = mr && (mr.x || mr.y || mr.z);

		if (hasMR) {
			// Apply base rotation (Collada built-in + panel angle) WITHOUT
			// modelRotation. rotation.x/y ARE mr.x/mr.y, so skip them.
			// rotation.z = panelAngle + mr.z, so subtract mr.z to get panelAngle.
			model.rotation.z = (rotation.z ?? 0) - (mr.z || 0);

			// Compose modelRotation in the model's LOCAL frame via quaternion
			// multiplication. This makes modelRotation panel-independent.
			const baseQuat = model.quaternion.clone();
			const modelRotQuat = new Quaternion().setFromEuler(new Euler(mr.x, mr.y, mr.z, 'XYZ'));
			model.quaternion.copy(baseQuat.multiply(modelRotQuat));
		} else {
			if (rotation.x) model.rotation.x += rotation.x;
			if (rotation.y) model.rotation.y += rotation.y;
			model.rotation.z = rotation.z ?? 0;
		}
	}

	// Apply per-instance rotation with bbox orbit so the model spins
	// around its visual center on rebuild (matching the drag behavior).
	const ir = metadata?.instanceRotation as { x: number; y: number; z: number } | undefined;
	if (ir && (ir.x || ir.y || ir.z)) {
		const irAngle = ir.z || ir.y || ir.x;

		const bbox = new Box3().setFromObject(model);
		const bboxCenter = new Vector3();
		bbox.getCenter(bboxCenter);
		const worldAxis = new Vector3(0, 1, 0);
		const offset = model.position.clone().sub(bboxCenter);
		offset.applyAxisAngle(worldAxis, irAngle);
		model.position.copy(bboxCenter).add(offset);

		if (ir.x) model.rotation.x += ir.x;
		if (ir.y) model.rotation.y += ir.y;
		if (ir.z) model.rotation.z += ir.z;
	}

	if (metadata) {
		model.userData = { ...model.userData, ...metadata };
	}

	applyShadows(model, config.qualitySettings, castShadow, receiveShadow);

	adjustModelMaterials(model, config.materialsMap);

	if (self.wireframeMode) {
		applyWireframeToModel(model, true);
	}

	if (self.environmentMap) {
		applyEnvironmentMapToModel(model, self.environmentMap);
	}

	if (parent) {
		parent.add(model);
	}

	if (metadata?.id) {
		self.loadedModels.set(metadata.id, model);
	}
};

/**
 * Load a Collada (DAE) model
 */
export const loadColladaCfn = (self: ModelManager, params: LoadColladaParams): Promise<Object3D> => {
	const { options } = params;
	const { path } = options;
	const { config } = self;
	const fullPath = path.startsWith('/') || path.startsWith('http') ? path : `${config.basePath}${path}`;

	// Return a clone of the cached scene graph to skip re-parsing.
	// The cached original already has merged vertices, so the clone inherits them.
	if (self.colladaCache.has(path)) {
		const cached = self.colladaCache.get(path) as Object3D;
		const clone = cached.clone();
		applyModelOptions(clone, self, options);
		return Promise.resolve(clone);
	}

	return new Promise((resolve, reject) => {
		const loader = new ColladaLoader();

		const originalWarn = console.warn;
		console.warn = createSuppressedWarn(originalWarn);

		loader.load(
			fullPath,
			(collada) => {
				console.warn = originalWarn;

				const model: Scene = collada.scene;

				// Smoothing replaces mesh geometry, so run it before caching — the pristine clone then shares the merged geometry.
				if ((config.qualityLevel ?? 0) >= 4) {
					smoothNormalsForMetalMeshes(model, config.qualityLevel);
				}

				// Cache a pristine clone BEFORE applying options — caching the mutated original bakes this instance's transforms/metadata into every future clone.
				self.colladaCache.set(path, model.clone());

				applyModelOptions(model, self, options);
				resolve(model);
			},
			undefined,
			(error) => {
				console.warn = originalWarn;
				console.error('Error loading Collada model:', path, error);
				reject(error);
			}
		);
	});
};

const adjustModelMaterials = (model: Object3D, materialsMap?: Map<string, any>): void => {
	if (!materialsMap) return;

	model.traverse((child: any) => {
		if (child.type === 'Mesh' && child.material) {
			if (Array.isArray(child.material)) {
				child.material = child.material.map((m: any) => adjustSingleMaterial(m, materialsMap));
			} else {
				child.material = adjustSingleMaterial(child.material, materialsMap);
			}
			// The arch mesh above the WOOD/LOG-INSERT fireplace is neutralized
			// to a transparent material, but Three.js's shadow pass ignores
			// opacity. Hide the mesh outright so it casts no shadow.
			const mats = Array.isArray(child.material) ? child.material : [child.material];
			if (mats.some((m: any) => m?.name === 'Image-arch')) child.visible = false;
		}
	});
};
